import {
  Box,
  DataTable,
  HairlineCard,
  Link,
  Stack,
  StatusChip,
  statusTone,
  Typography,
  type StatusKind,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useCompanies } from '../../lib/administration-api';
import { useCompanyBilling } from '../../lib/seats-api';
import { useScope } from '../../providers/ScopeProvider';
import { SeatCount } from './SeatsPanel';
import { frenchFirstOfMonth } from '@munifocus/utils';

function money(cents: number | null, currency: string, locale: string): string {
  if (cents === null) return '—';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

/**
 * The company's Munifocus licence and invoices — what its administrator needs
 * to handle billing, readable without holding an enforcement seat.
 *
 * Read-only by design: the licence is changed with Munifocus, and an invoice is
 * paid through the payment page Stripe hosts for it.
 */
export function CompanyBillingPanel() {
  const { t, i18n } = useTranslation();
  const { active } = useScope();
  const companies = useCompanies(active?.kind !== 'company');
  const companyId = active?.kind === 'company' ? active.id : (companies.data?.[0]?.id ?? null);
  const billing = useCompanyBilling(companyId);
  const data = billing.data;
  const locale = i18n.language?.startsWith('fr') ? 'fr-CA' : 'en-CA';
  const french = locale === 'fr-CA';

  return (
    <Stack spacing={2}>
      <HairlineCard sx={{ p: 2.5 }}>
        <Typography variant="h3" sx={{ fontSize: 16, mb: 1 }}>
          {t('gov.admin.billing.licence')}
        </Typography>
        {!data ? (
          <Typography variant="body2" color="text.secondary">
            {billing.isLoading ? t('gov.admin.billing.loading') : '—'}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              <SeatCount used={data.seats.used} limit={data.seats.limit} />
            </Typography>
            {data.plans.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('gov.admin.billing.noPlan')}
              </Typography>
            ) : (
              data.plans.map((plan) => (
                <Box key={plan.subscriptionId}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {french ? plan.planNameFr : plan.planNameEn}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="p">
                    {t('gov.admin.billing.planLine', {
                      price: money(plan.unitAmountCents, plan.currency, locale),
                      interval: t(
                        plan.interval === 'month'
                          ? 'gov.admin.billing.perMonth'
                          : 'gov.admin.billing.perYear',
                      ),
                      seats: plan.includedSeats,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="p">
                    {plan.extraSeatUnitAmountCents === null
                      ? t('gov.admin.billing.extraSeatsTbd', { count: plan.extraSeats })
                      : t('gov.admin.billing.extraSeats', {
                          count: plan.extraSeats,
                          price: money(plan.extraSeatUnitAmountCents, plan.currency, locale),
                        })}
                  </Typography>
                </Box>
              ))
            )}
            {data.seats.complimentarySeats > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('gov.admin.billing.complimentary', { count: data.seats.complimentarySeats })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {data.onboardingFee.charged
                ? t('gov.admin.billing.onboardingCharged', {
                    amount: money(data.onboardingFee.amountCents, 'CAD', locale),
                  })
                : t('gov.admin.billing.onboardingNotCharged')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('gov.admin.billing.moreSeats')}
            </Typography>
          </Stack>
        )}
      </HairlineCard>

      <DataTable
        rows={data?.invoices ?? []}
        getRowKey={(row) => row.id}
        isLoading={billing.isLoading}
        emptyLabel={t('gov.admin.billing.noInvoices')}
        initialSortKey="issued"
        columns={[
          {
            key: 'number',
            label: t('gov.admin.billing.invoice'),
            primary: true,
            mono: true,
            sortValue: (row) => row.number ?? '',
            cell: (row) =>
              row.hostedInvoiceUrl ? (
                <Link href={row.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  {row.number ?? t('gov.admin.billing.view')}
                </Link>
              ) : (
                (row.number ?? '—')
              ),
          },
          {
            key: 'issued',
            label: t('gov.admin.billing.issued'),
            nowrap: true,
            sortValue: (row) => row.issuedAt ?? '',
            cell: (row) =>
              row.issuedAt
                ? frenchFirstOfMonth(new Date(row.issuedAt).toLocaleDateString(locale))
                : '—',
          },
          {
            key: 'status',
            label: t('gov.admin.billing.status'),
            sortValue: (row) => row.status,
            cell: (row) => (
              <StatusChip
                label={t(`gov.admin.billing.statuses.${row.status}`, row.status)}
                status={(statusTone.invoice as Record<string, StatusKind>)[row.status] ?? 'neutral'}
              />
            ),
          },
          {
            key: 'total',
            label: t('gov.admin.billing.total'),
            align: 'right',
            nowrap: true,
            sortValue: (row) => row.totalCents,
            cell: (row) => money(row.totalCents, row.currency, locale),
          },
          {
            key: 'due',
            label: t('gov.admin.billing.due'),
            align: 'right',
            nowrap: true,
            sortValue: (row) => row.amountDueCents,
            cell: (row) => money(row.amountDueCents, row.currency, locale),
          },
        ]}
      />
    </Stack>
  );
}
